import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X } from 'lucide-react';

/**
 * Pulsing info hotspot.
 *
 * Props:
 *  - text: tooltip body
 *  - position: 'top' | 'bottom' | 'left' | 'right'
 *  - color: tailwind color accent
 *  - autoOpenAt: seconds — when set, the tooltip auto-opens at this delay
 *  - autoCloseAfter: seconds — when set with autoOpenAt, tooltip auto-closes after this many seconds
 *  - glowAt: seconds — when set, the dot pulses an extra "look at me" ring at this time
 *
 * Global: when `window.__PDF_EXPORTING__` is true, every InfoSpot renders
 * its tooltip open so the exported PDF shows all info.
 */
export default function InfoSpot({
  text, position = 'top', color = 'teal',
  autoOpenAt = null, autoCloseAfter = 2.5, glowAt = null,
}) {
  const [open, setOpen] = useState(false);
  const [glowing, setGlowing] = useState(false);
  const [exportMode, setExportMode] = useState(false);

  // Detect PDF export mode via a global flag polled every 150ms.
  useEffect(() => {
    const id = setInterval(() => {
      const flag = typeof window !== 'undefined' && window.__PDF_EXPORTING__;
      setExportMode(Boolean(flag));
    }, 150);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (autoOpenAt == null) return;
    const openT = setTimeout(() => setOpen(true), autoOpenAt * 1000);
    const closeT = setTimeout(() => setOpen(false), (autoOpenAt + autoCloseAfter) * 1000);
    return () => { clearTimeout(openT); clearTimeout(closeT); };
  }, [autoOpenAt, autoCloseAfter]);

  useEffect(() => {
    if (glowAt == null) return;
    const on = setTimeout(() => setGlowing(true), glowAt * 1000);
    const off = setTimeout(() => setGlowing(false), (glowAt + 1.6) * 1000);
    return () => { clearTimeout(on); clearTimeout(off); };
  }, [glowAt]);

  const colors = {
    teal:  { dot: 'bg-teal-500',  ring: 'ring-teal-300',  bg: 'bg-teal-600',  text: 'text-white' },
    amber: { dot: 'bg-amber-500', ring: 'ring-amber-300', bg: 'bg-amber-600', text: 'text-white' },
    blue:  { dot: 'bg-blue-500',  ring: 'ring-blue-300',  bg: 'bg-blue-600',  text: 'text-white' },
    red:   { dot: 'bg-red-500',   ring: 'ring-red-300',   bg: 'bg-red-600',   text: 'text-white' },
  };
  const c = colors[color] || colors.teal;

  const positionClasses = {
    top:    'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left:   'right-full mr-2 top-1/2 -translate-y-1/2',
    right:  'left-full ml-2 top-1/2 -translate-y-1/2',
  };

  return (
    <span className="relative inline-flex items-center" style={{ zIndex: open ? 50 : 10 }}>
      {/* Extra "look at me" glow ring when scripted */}
      {glowing && (
        <motion.span
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: [0, 0.8, 0], scale: [0.6, 2.4, 3] }}
          transition={{ duration: 1.4, ease: 'easeOut' }}
          className={`absolute inset-0 rounded-full ${c.dot} pointer-events-none`}
        />
      )}

      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={`w-5 h-5 rounded-full ${c.dot} ring-2 ${c.ring} flex items-center justify-center cursor-pointer hover:scale-110 transition-transform`}
      >
        {open
          ? <X size={10} className="text-white" />
          : <motion.div animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <Info size={10} className="text-white" />
            </motion.div>
        }
      </button>

      <AnimatePresence>
        {(open || exportMode) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: position === 'top' ? 6 : -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute ${positionClasses[position]} w-64 px-3 py-2.5 rounded-xl ${c.bg} shadow-xl z-[60]`}
          >
            <p className={`text-[12px] font-body leading-relaxed ${c.text}`}>{text}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}
